<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Test Broadcast
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div>
                <UInput
                    autofocus
                    v-model="search"
                    :loading="searching"
                    size="lg" 
                    placeholder="Search customers by name or phone..."
                    icon="i-ph-magnifying-glass-duotone"
                />

                <div v-if="search && !searching" class="mt-4">
                    <div v-if="!customers.length" class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                        <UIcon name="i-ph-magnifying-glass-minus-duotone" class="mx-auto size-7 text-gray-400" />
                        <h3 class="mt-2 text-sm font-medium text-gray-900">No results</h3>
                        <p class="mt-1 text-xs text-gray-500">Try a different search term.</p>
                    </div>
    
                    <div v-else class="flex-1">
                        <button
                            type="button"
                            v-for="customer in customers" 
                            :key="customer.id" 
                            @click="selectedCustomer = customer" 
                            class="-mt-px relative w-full px-5 py-4 border first:rounded-t-md last:rounded-b-md space-y-3 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-primary transition-colors"
                        >
                            <div class="flex items-center space-x-3">
                                <Avatar 
                                    class="shrink-0"
                                    size="md"
                                    :path="customer.avatar" 
                                    :placeholder="[customer.first_name, customer.last_name].filter(Boolean).join(' ')" 
                                />
    
                                <div class="flex-1 leading-4 text-left">
                                    <p class="font-medium text-sm">{{ [customer.first_name, customer.middle_name, customer.last_name].filter(Boolean).join(' ') }}</p>
                                </div>
    
                                <UIcon v-if="selectedCustomer?.id === customer.id" name="i-ph-check-bold" class="size-5 text-gray-700" />
                            </div>
                            <div class="space-y-1 text-sm">
                                <Field v-if="customer.phone" inline label="Phone" label-class="text-[11px] uppercase text-gray-400">
                                    <PhoneLabel :value="customer.phone" />
                                </Field>
                                <Field v-if="customer.email" inline label="Email" label-class="text-[11px] uppercase text-gray-400">
                                    <span>{{ customer.email }}</span>
                                </Field>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white">
                        Cancel
                    </UButton>
                    <UButton @click="confirm()" size="lg" :disabled="!selectedCustomer" :loading="loading" color="black">
                        Send
                    </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Broadcast } from '~/types/broadcast'
import type { Customer } from '~/types/customer';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    broadcast?: Broadcast
}>()

const client = useSupabaseClient()
const toast = useToast()
const loading = ref<boolean>(false)
const search = ref<string>('')
const searching = ref<boolean>(false)
const searchTimeout = ref<NodeJS.Timeout>()
const customers = ref<Customer[]>([])
const selectedCustomer = ref()

watch(() => props.open, () => {
    reset()
})

watch(() => search.value, (value) => {
    if (value) {
        fetchCustomers()
    }
    else {
        reset()
    }
})

async function fetchCustomers() {
    if (search.value) {
        searching.value = true

        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value)
        }

        searchTimeout.value = setTimeout(async () => {
            const searchQuery = search.value.trim()
            const numberQuery = parseInt(search.value.replace(/[^\d]/g, ''))

            const { data } = await client.from('customers')
                .select(`id, number, user_id, first_name, middle_name, last_name, email, phone`)
                .or([
                    `fts.plfts(simple).'${searchQuery}'`,
                    `email.ilike.%${searchQuery}%`,
                    !isNaN(numberQuery) ? `phone.ilike.%${numberQuery}` : undefined,
                    !isNaN(numberQuery) && numberQuery <= 2147483647 ? `number.eq.${numberQuery}` : undefined
                ].filter(Boolean).join(','))
                .returns<Customer[]>()

            if (data) {
                customers.value = data
            }
            
            searching.value = false
        }, 1000)
    }
}

function reset() {
    search.value = ''
    searching.value = false
    customers.value = []
    selectedCustomer.value = undefined
}

async function confirm() {
    if (props.broadcast) {
        loading.value = true

        try {
            const result = await $fetch<{ ok: boolean; error?: string }>('/api/broadcasts/test', {
                method: 'POST',
                body: {
                    id: props.broadcast.id,
                    customer_id: selectedCustomer.value?.id
                }
            })
            if (result.ok) {
                toast.add({
                    title: 'Test message sent.',
                    icon: 'i-ph-check-circle-duotone'
                })
            } else {
                toast.add({
                    title: result.error || 'Send failed.',
                    color: 'red',
                    icon: 'i-ph-warning-duotone'
                })
            }
        } catch (error: any) {
            toast.add({
                title: 'Could not send a test message, please try again later.',
                color: 'red',
                icon: 'i-ph-warning-duotone'
            })
        } finally {
            loading.value = false
            dismiss()
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>